import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionDeleteIcon } from '../../../assets/icons/action-delete.svg';
import { ReactComponent as ActionEditIcon } from '../../../assets/icons/action-edit.svg';
import { ReactComponent as DocumentIcon } from '../../../assets/icons/document.svg';
import { ReactComponent as DocumentIconDark } from '../../../assets/icons/iconsDark/document-dark.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/location-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import ThemeContext from '../../../contexts/theme-context/ThemeContext';
import {
  AvailableStation,
  RequestStationInterface,
} from '../../../interface/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchApprovedStations,
  setApprovedStationFilterData,
  updateStationFilters,
  fetchListFilters,
  disableFiltersApplied,
  fetchAllStation,
  setSortData
} from '../../../redux/slice/ApprovedStationManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { RootState } from '../../../redux/store';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DropDownMenu from '../../../shared-components/drop-down-menu/DropDownMenu';
import ExportButton from '../../../shared-components/export-button/ExportButton';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import stationApi from '../../../utils/api/station';

/**
 * Add new station component
 */
function ApprovedList({
  handleDeleteStationWarning,
  renderAvailability,
}: {
  handleDeleteStationWarning: (value: string | undefined) => void;
  renderAvailability: (value: AvailableStation) => JSX.Element;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    stations,
    page,
    limit,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
  } = useAppSelector((state) => state.approvedStation);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { sortBy, sortField } = useAppSelector((state: RootState) => state.approvedStation);

  /**
   * Navigate to edit station
   */
  const handleEditStation = (rowId?: string | null) => {
    if (rowId) {
      navigate(`../edit/approved/${rowId}`);
    }
  };
  const { theme } = useContext(ThemeContext);

  /**
   * Handle the fetch the data function
   * @param data
   */
  const fetchData = (data: RequestStationInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchApprovedStations({
          ...data,
        }),
      );
    }
  };

  const column = [
    {
      label: 'S. No',
      accessorKey: 's_no' as keyof AvailableStation,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (station: AvailableStation, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: 'Station name',
      accessorKey: 'name' as keyof AvailableStation,
      sortAllow: true,
      queryParamsKey: 'stationId',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Land Document',
      accessorKey: 'landOwnerShipDocument' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station?.landOwnerShipDocument?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>{theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}</span>
              <p className="table-row-each-data">{station?.landOwnerShipDocument?.[0]?.fileName}</p>
            </div>
            {station?.landOwnerShipDocument?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.landOwnerShipDocument.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
    },
    {
      label: 'Eb Reports',
      accessorKey: 'ebReport' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Document view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station?.ebReport?.length ? (
          <div className="document-in-station-table">
            <div className="document-container">
              <span>{theme === 'light' ? <DocumentIcon /> : <DocumentIconDark />}</span>
              <p className="table-row-each-data">{station?.ebReport?.[0]?.fileName}</p>
            </div>
            {station?.ebReport?.length > 1 && (
              <div className="document-count-wrap">
                <p className="table-row-each-data">+{station.ebReport.length - 1}</p>
              </div>
            )}
          </div>
        ) : null,
    },
    {
      label: 'Electricity type',
      accessorKey: 'electricityContractType' as keyof AvailableStation,
      sortAllow: true,
    },
    {
      label: 'Operating Hrs',
      accessorKey: 'opensAt' as keyof AvailableStation,
      sortAllow: true,
      /**
       * Time view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.opensAt && station.closesAt ? (
          <p className="table-row-each-data">{`${FormatUtcString(station.opensAt)} - ${FormatUtcString(station.closesAt)}`}</p>
        ) : null,
    },
    {
      label: 'Location',
      accessorKey: 'address' as keyof AvailableStation,
      sortAllow: true,
      /**
       * Location view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{station.address}</p>
          </div>
        ) : null,
    },
    {
      label: 'Media',
      accessorKey: 'medias' as keyof AvailableStation,
      hideWhenSidePanelOpen: true,
      /**
       * Media view in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) =>
        station.medias ? (
          <div className="medias-in-station-table">
            {(() => {
              let selectedFilesView;
              if (station.medias && station.medias?.length <= 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias?.map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label="English captions"
                                  default
                                />
                                Your browser does not support the video tag.
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                  </div>
                );
              } else if (station.medias && station.medias?.length > 4) {
                selectedFilesView = (
                  <div className="selected-files-view-wrap">
                    {station.medias.slice(0, 3).map((media) => (
                      <div className="single-selected-wrap">
                        {(() => {
                          let fileTypeView;
                          if (media?.fileType?.includes('image')) {
                            fileTypeView = (
                              <img src={media.signedUrl} alt="" className="selected-image" />
                            );
                          } else if (media?.fileType?.includes('video')) {
                            fileTypeView = (
                              <video controls={false} className="selected-video" preload="none">
                                <source src={media.signedUrl} type="video/mp4" />
                                <track
                                  src="path/to/captions.vtt"
                                  kind="captions"
                                  label="English captions"
                                  default
                                />
                                Your browser does not support the video tag.
                              </video>
                            );
                          }
                          return fileTypeView;
                        })()}
                      </div>
                    ))}
                    <div className="single-selected-wrap">
                      <p className="number-of-files">+{station.medias.length - 3}</p>
                    </div>
                  </div>
                );
              }
              return selectedFilesView;
            })()}
          </div>
        ) : null,
    },
    {
      label: 'Availability',
      accessorKey: 'isAvailable' as keyof AvailableStation,
      customData: renderAvailability,
    },
    {
      label: 'Actions',
      accessorKey: '' as keyof AvailableStation,
      columnWidth: 60,
      /**
       * Action button in table
       * @param station
       * @param index
       */
      customData: (station: AvailableStation) => (
        <DropDownMenu
          rowId={station.id}
          dropDownList={[
            {
              icon: <ActionEditIcon />,
              content: 'Edit',
              onClickFunc: handleEditStation,
              className: 'primary-action',
            },
            {
              icon: <ActionDeleteIcon />,
              content: 'Delete',
              /**
               *
               */
              onClickFunc: handleDeleteStationWarning,
            },
          ]}
          lineBetweenElement
        />
      ),
    },
  ];

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setApprovedStationFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    stationApi
      .fetchStationsExport({ ...filters, submissionStatus: 'approved' })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Station name',
          'Land Document',
          'Eb Reports',
          'Electricity type',
          'Operating Hrs',
          'Location',
          'Availability',
        ];
        const rows = response.data.results.map((row: AvailableStation) => [
          row.name || '',
          row.landOwnerShipDocument && row.landOwnerShipDocument.length > 0
            ? row.landOwnerShipDocument[0]?.fileName
            : '',
          row.ebReport && row.ebReport.length > 0 ? row.ebReport[0]?.fileName : '',
          row.electricityContractType || '',
          row.opensAt && row.closesAt
            ? `${FormatUtcString(ConvertUTCtoHHMM(row.opensAt))} - ${FormatUtcString(ConvertUTCtoHHMM(row.closesAt))}`
            : '',
          row.address || '',
          row.isAvailable ? 'Available' : 'Unavailable',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'approved-station-list.csv');
          } else {
            exportPDF(headers, rows, 'approved-station-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    if (isFilterUpdated || stations.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(
        fetchAllStation({
          ...queryParameters,
          page,
          limit,
        }),
      );

      dispatch(disableFiltersApplied());
    }
  }, [currentFacilityTagsList, filters]);

  return (
    <CustomTable
      paginate
      loading={loading}
      limit={limit}
      header={column}
      data={stations}
      currentPage={page}
      sortData={{
        sortBy,
        sortField
      }}
      handleSortChange={handleSortFunction}
      totalPages={totalPages}
      totalResults={totalResults}
      handlePageChange={handlePageChange}
      handleLimitChange={handleLimitChange}
      footerLeft={
        <div className="table-bottom-button-list">
          <div className="table-button-item">
            <ExportButton
              label="Export Data"
              buttonList={[
                {
                  key: 'pdf',
                  allow: true,
                  /**
                   * Export pdf function
                   * @returns
                   */
                  onClickFunction: () => exportDataPdfOrCSV('pdf'),
                },
                {
                  key: 'csv',
                  allow: true,
                  /**
                   * Export csv function
                   * @returns
                   */
                  onClickFunction: () => exportDataPdfOrCSV('csv'),
                },
              ]}
              disabled={stations.length === 0}
              loader={exportLoader}
            />
          </div>
        </div>
      }
      topHeader={false}
      filters={filters}
      filtersToDispatch={updateStationFilters}
      filterListLoading={filterListLoading}
      filterListToDispatch={fetchListFilters}
    />
  );
}

export default ApprovedList;
